/**
 * Per-route metadata. ONE implementation, used by:
 *   - the meta/SSR server (to inject <head> into the served HTML), and
 *   - the client (to keep the document head correct during SPA navigation).
 *
 * This is the structural fix for B1 (identical OG tags on every article) and
 * B6 (missing canonical). Canonical URLs are ALWAYS absolute, and every route
 * that renders content produces a canonical built from its own slug.
 */

export const SITE_NAME = 'The Story Line';
export const SITE_TAGLINE = 'Stories, art and culture, told well.';
export const DEFAULT_DESCRIPTION =
  'The Story Line is a literary, art and entertainment publication: essays, reviews, interviews and culture reporting, told well.';
export const DEFAULT_OG_IMAGE = '/og/default.png';

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function abs(siteUrl, p) {
  if (!p) return siteUrl;
  if (/^https?:\/\//i.test(p)) return p;
  return `${siteUrl.replace(/\/$/, '')}${p.startsWith('/') ? p : `/${p}`}`;
}

function clamp(s, n) {
  const str = String(s ?? '').replace(/\s+/g, ' ').trim();
  return str.length <= n ? str : `${str.slice(0, n - 1).trimEnd()}\u2026`;
}

/**
 * @param {{ route: {name:string, params?:Record<string,any>},
 *            article?: any, category?: any,
 *            siteUrl: string, siteName?: string }} input
 */
export function buildMeta(input) {
  const siteUrl = input.siteUrl;
  const siteName = input.siteName || SITE_NAME;
  const { route, article, category } = input;

  /** @type {any} */
  const meta = {
    title: siteName,
    description: DEFAULT_DESCRIPTION,
    canonical: abs(siteUrl, '/'),
    robots: 'index,follow',
    ogType: 'website',
    ogImage: abs(siteUrl, DEFAULT_OG_IMAGE),
    publishedTime: null,
    modifiedTime: null,
    author: null,
    jsonLd: null,
  };

  if (route.name === 'article' && article) {
    const published = article.publishedAt || article.scheduledFor || null;
    meta.title = `${article.seo?.title || article.title} | ${siteName}`;
    meta.description = clamp(article.seo?.description || article.dek || article.excerpt || '', 300);
    meta.canonical = abs(siteUrl, `/article/${article.slug}`);
    meta.ogType = 'article';
    meta.ogImage = abs(siteUrl, article.seo?.ogImage || article.coverImageUrl || DEFAULT_OG_IMAGE);
    meta.publishedTime = published;
    meta.modifiedTime = article.updatedAt || published;
    meta.author = article.author?.name || null;
    meta.noindex = !!article.seo?.noindex;
    meta.robots = article.seo?.noindex ? 'noindex,nofollow' : 'index,follow';
    meta.jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: clamp(article.title, 110),
      description: meta.description,
      image: [meta.ogImage],
      datePublished: published || undefined,
      dateModified: article.updatedAt || published || undefined,
      author: article.author ? [{ '@type': 'Person', name: article.author.name, url: abs(siteUrl, `/author/${article.author.slug}`) }] : undefined,
      publisher: { '@type': 'Organization', name: siteName, url: siteUrl },
      mainEntityOfPage: { '@type': 'WebPage', '@id': meta.canonical },
      articleSection: category?.name || undefined,
      inLanguage: article.languageCode || undefined,
    };
  } else if (route.name === 'category' && category) {
    meta.title = `${category.name} | ${siteName}`;
    meta.description = clamp(category.description || `${category.name} from ${siteName}.`, 300);
    meta.canonical = abs(siteUrl, `/category/${category.slug}`);
    meta.jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: category.name,
      url: meta.canonical,
      isPartOf: { '@type': 'WebSite', name: siteName, url: siteUrl },
    };
  } else if (route.name === 'home') {
    meta.title = `${siteName} — Literature, Art & Entertainment`;
    meta.canonical = abs(siteUrl, '/');
    meta.jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: siteName,
      url: siteUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl.replace(/\/$/, '')}/search?q={query}`,
        'query-input': 'required name=query',
      },
    };
  } else if (route.name === 'search') {
    // Search result pages must never be indexed.
    meta.title = `Search | ${siteName}`;
    meta.canonical = abs(siteUrl, '/search');
    meta.robots = 'noindex,follow';
  } else if (route.name === 'notFound') {
    meta.title = `Page not found | ${siteName}`;
    meta.canonical = abs(siteUrl, route.path || '/');
    meta.robots = 'noindex,follow';
  } else {
    const labels = {
      about: 'About', contact: 'Contact', terms: 'Terms', privacy: 'Privacy', advertise: 'Advertise',
      latest: 'Latest', trending: 'Trending',
    };
    const label = labels[route.name];
    if (label) {
      meta.title = `${label} | ${siteName}`;
      meta.canonical = abs(siteUrl, `/${route.name}`);
    }
  }
  return meta;
}

/** Serialize a meta object to <head> HTML (server) — same shape the client applies. */
export function renderHeadTags(meta) {
  const t = [];
  t.push(`<title>${esc(meta.title)}</title>`);
  t.push(`<meta name="description" content="${esc(meta.description)}" />`);
  t.push(`<meta name="robots" content="${esc(meta.robots)}" />`);
  t.push(`<link rel="canonical" href="${esc(meta.canonical)}" />`);
  t.push(`<meta property="og:site_name" content="${esc(SITE_NAME)}" />`);
  t.push(`<meta property="og:type" content="${esc(meta.ogType)}" />`);
  t.push(`<meta property="og:title" content="${esc(meta.title)}" />`);
  t.push(`<meta property="og:description" content="${esc(meta.description)}" />`);
  t.push(`<meta property="og:url" content="${esc(meta.canonical)}" />`);
  t.push(`<meta property="og:image" content="${esc(meta.ogImage)}" />`);
  if (meta.publishedTime) t.push(`<meta property="article:published_time" content="${esc(meta.publishedTime)}" />`);
  if (meta.modifiedTime) t.push(`<meta property="article:modified_time" content="${esc(meta.modifiedTime)}" />`);
  if (meta.author) t.push(`<meta property="article:author" content="${esc(meta.author)}" />`);
  t.push(`<meta name="twitter:card" content="summary_large_image" />`);
  t.push(`<meta name="twitter:title" content="${esc(meta.title)}" />`);
  t.push(`<meta name="twitter:description" content="${esc(meta.description)}" />`);
  t.push(`<meta name="twitter:image" content="${esc(meta.ogImage)}" />`);
  if (meta.jsonLd) {
    t.push(`<script type="application/ld+json">${JSON.stringify(meta.jsonLd).replace(/</g, '\\u003c')}</script>`);
  }
  return t.join('\n    ');
}
